// File: frontend/src/views/private/Chats.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/apiService';
import { formatDate } from '../../utils/formatters';
import PageHeader from '../../components/layout/PageHeader';
import PageContainer from '../../components/layout/PageContainer';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import {
  MessageSquare, AlertCircle, Clock, ChevronRight,
  Shield, ClipboardList, Receipt, Bot, User, MessagesSquare,
  FileText, Layers
} from 'lucide-react';
import styles from './Chats.module.css';

const Chats = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('all'); // New state for filtering

  useEffect(() => {
    loadChats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadChats = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getUserChats({}, user.user_id);

      if (!response.success) {
        throw new Error(response.error || 'Failed to load conversations');
      }

      setChats(response.data.chats || []);
    } catch (err) {
      console.error('Load chats error:', err);
      setError(err.message || 'Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChat = (chatId) => {
    navigate(`/dashboard/chat/${chatId}?from=chats`);
  };

  // const handleNewChat = () => {
  //   window.dispatchEvent(new Event('openChatWidget'));
  // };

  // ── Agent display helpers ──
  const getAgentConfig = (agentName) => {
    const configs = {
      ai_allegoryinsurance_policy_assistant: {
        label: 'Policy Assistant',
        shortLabel: 'Policy',
        icon: Shield,
        colorClass: styles.agentPolicy,
        accentClass: styles.accentPolicy,
      },
      ai_allegoryinsurance_claims_assistant: {
        label: 'Claims Assistant',
        shortLabel: 'Claims',
        icon: ClipboardList,
        colorClass: styles.agentClaims,
        accentClass: styles.accentClaims,
      },
      ai_allegoryinsurance_quote_assistant: {
        label: 'Quote Assistant',
        shortLabel: 'Quote',
        icon: Receipt,
        colorClass: styles.agentQuote,
        accentClass: styles.accentQuote,
      },
      ai_allegoryinsurance_web_widget: {
        label: 'General Assistant',
        shortLabel: 'General',
        icon: MessageSquare,
        colorClass: styles.agentGeneral,
        accentClass: styles.accentGeneral,
      },
      ai_metadata_extraction: {
        label: 'Document Analysis',
        shortLabel: 'Analysis',
        icon: FileText,
        colorClass: styles.agentAnalysis,
        accentClass: styles.accentAnalysis,
      },
    };
    return configs[agentName] || {
      label: formatAgentName(agentName),
      shortLabel: formatAgentName(agentName),
      icon: Bot,
      colorClass: styles.agentDefault,
      accentClass: styles.accentDefault,
    };
  };

  const formatAgentName = (name) => {
    if (!name) return 'AI Assistant';
    return name
      .replace(/^ai_allegoryinsurance_/, '')
      .replace(/^ai_/, '')
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  const getStatusConfig = (status) => {
    if (status === 'active') return { label: 'Active', className: styles.statusActive };
    if (status === 'closed' || status === 'ended') return { label: 'Ended', className: styles.statusEnded };
    return { label: status || 'Active', className: styles.statusActive };
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr, 'short');
  };

  const truncatePreview = (text, maxLength = 120) => {
    if (!text) return null;
    // Strip markdown formatting for preview
    const clean = text
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .replace(/#{1,6}\s/g, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/`/g, '')
      .trim();
    if (clean.length <= maxLength) return clean;
    return clean.substring(0, maxLength).trim() + '…';
  };

  // Filter out metadata extraction chats
  const userChats = chats.filter(c => c.agent_name !== 'ai_metadata_extraction');

  // Apply agent filter
  const filteredChats = selectedAgent === 'all' 
    ? userChats 
    : userChats.filter(c => c.agent_name === selectedAgent);

  // Separate active from ended chats and sort by date (newest first)
  const activeChats = filteredChats
    .filter(c => c.chat_status === 'active')
    .sort((a, b) => new Date(b.last_message_at || b.created_at) - new Date(a.last_message_at || a.created_at));
  
  const endedChats = filteredChats
    .filter(c => c.chat_status !== 'active')
    .sort((a, b) => new Date(b.last_message_at || b.created_at) - new Date(a.last_message_at || a.created_at));

  // Group all user chats by agent type (for filter pills)
  const agentGroups = {};
  userChats.forEach(chat => {
    const agent = chat.agent_name || 'unknown';
    if (!agentGroups[agent]) agentGroups[agent] = 0;
    agentGroups[agent]++;
  });

  if (isLoading) {
    return (
      <PageContainer>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader
          title="Conversations"
          subtitle="Your chat history with AI assistants"
          icon={<MessagesSquare size={24} strokeWidth={2} />}
          showBack={true}
          backTo="/dashboard"
        />
        <div className={styles.errorContainer}>
          <div className={styles.errorCard}>
            <AlertCircle size={48} className={styles.errorIcon} />
            <h2>Failed to Load Conversations</h2>
            <p>{error}</p>
            <button onClick={loadChats} className={styles.retryButton}>
              Try Again
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Conversations"
        subtitle={userChats.length > 0
          ? `${userChats.length} conversation${userChats.length !== 1 ? 's' : ''} with AI assistants`
          : 'Your chat history with AI assistants'
        }
        icon={<MessagesSquare size={24} strokeWidth={2} />}
        showBack={true}
        backTo="/dashboard"
        // actions={
        //   <button onClick={handleNewChat} className={styles.newChatButton}>
        //     <Plus size={18} strokeWidth={2.5} />
        //     <span>New Chat</span>
        //   </button>
        // }
      />

      {userChats.length === 0 ? (
        <div className={styles.emptyContainer}>
          <div className={styles.emptyCard}>
            <div className={styles.emptyIconWrap}>
              <MessagesSquare size={48} strokeWidth={1.5} />
            </div>
            <h2>No conversations yet</h2>
            <p>Start chatting with our AI assistants about your policies, claims, or quotes</p>
            {/* <button onClick={handleNewChat} className={styles.newChatButtonLarge}>
              <Sparkles size={20} strokeWidth={2} />
              Start Your First Conversation
            </button> */}
          </div>
        </div>
      ) : (
        <div className={styles.chatsContent}>
          {/* ── Filter Pills ── */}
          {Object.keys(agentGroups).length > 0 && (
            <div className={styles.summaryStrip}>
              {/* All button */}
              <button
                className={`${styles.summaryChip} ${styles.summaryChipClickable} ${
                  selectedAgent === 'all' ? styles.summaryChipActive : ''
                }`}
                onClick={() => setSelectedAgent('all')}
              >
                <Layers size={16} />
                <span className={styles.summaryChipCount}>{userChats.length}</span>
                <span className={styles.summaryChipLabel}>All</span>
              </button>

              {/* Agent-specific filters */}
              {Object.entries(agentGroups).map(([agent, count]) => {
                const config = getAgentConfig(agent);
                const AgentIcon = config.icon;
                const isActive = selectedAgent === agent;
                
                return (
                  <button
                    key={agent}
                    className={`${styles.summaryChip} ${styles.summaryChipClickable} ${config.colorClass} ${
                      isActive ? styles.summaryChipActive : ''
                    }`}
                    onClick={() => setSelectedAgent(agent)}
                  >
                    <AgentIcon size={16} />
                    <span className={styles.summaryChipCount}>{count}</span>
                    <span className={styles.summaryChipLabel}>{config.shortLabel}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Show message if filter returns no results */}
          {filteredChats.length === 0 && selectedAgent !== 'all' && (
            <div className={styles.emptyFilterContainer}>
              <p className={styles.emptyFilterText}>
                No conversations found for this assistant.
              </p>
              <button 
                className={styles.clearFilterButton}
                onClick={() => setSelectedAgent('all')}
              >
                Show All Conversations
              </button>
            </div>
          )}

          {/* ── Active Conversations ── */}
          {activeChats.length > 0 && (
            <div className={styles.chatSection}>
              {endedChats.length > 0 && (
                <h3 className={styles.sectionTitle}>
                  <MessageSquare size={16} />
                  Active
                </h3>
              )}
              <div className={styles.chatsGrid}>
                {activeChats.map((chat) => {
                  const agentConfig = getAgentConfig(chat.agent_name);
                  const AgentIcon = agentConfig.icon;
                  const totalMessages = (chat.user_message_count || 0) + (chat.assistant_message_count || 0);

                  return (
                    <button
                      key={chat.chat_id}
                      className={styles.chatCard}
                      onClick={() => handleOpenChat(chat.chat_id)}
                    >
                      <div className={`${styles.cardAccent} ${agentConfig.accentClass}`} />

                      <div className={styles.cardBody}>
                        {/* Top row */}
                        <div className={styles.cardTopRow}>
                          <div className={`${styles.agentTag} ${agentConfig.colorClass}`}>
                            <AgentIcon size={14} />
                            <span>{agentConfig.label}</span>
                          </div>
                          <span className={styles.timeAgo}>{getTimeAgo(chat.last_message_at || chat.created_at)}</span>
                        </div>

                        {/* Preview */}
                        {chat.last_message_preview ? (
                          <p className={styles.messagePreview}>
                            {truncatePreview(chat.last_message_preview)}
                          </p>
                        ) : (
                          <p className={styles.messagePreviewEmpty}>
                            Conversation started
                          </p>
                        )}

                        {/* Stats row */}
                        <div className={styles.cardStatsRow}>
                          <div className={styles.messageCounts}>
                            {chat.user_message_count > 0 && (
                              <div className={styles.messageCount}>
                                <User size={11} />
                                <span>{chat.user_message_count}</span>
                              </div>
                            )}
                            {chat.assistant_message_count > 0 && (
                              <div className={styles.messageCount}>
                                <Bot size={11} />
                                <span>{chat.assistant_message_count}</span>
                              </div>
                            )}
                            {totalMessages > 0 && (
                              <span className={styles.totalMessages}>
                                {totalMessages} message{totalMessages !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div className={styles.viewArrow}>
                            <span>Continue</span>
                            <ChevronRight size={16} />
                          </div>
                        </div>

                        {/* Date */}
                        <div className={styles.cardDate}>
                          <span>Started {formatDate(chat.created_at, 'long')}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}

                {/* New chat card - commented out */}
                {/* <button className={styles.addChatCard} onClick={handleNewChat}>
                  <div className={styles.addChatIcon}>
                    <Plus size={28} strokeWidth={1.5} />
                  </div>
                  <span className={styles.addChatLabel}>New Conversation</span>
                </button> */}
              </div>
            </div>
          )}

          {/* ── Ended Conversations ── */}
          {endedChats.length > 0 && (
            <div className={styles.chatSection}>
              <h3 className={styles.sectionTitle}>
                <Clock size={16} />
                Previous
              </h3>
              <div className={styles.chatsGrid}>
                {endedChats.map((chat) => {
                  const agentConfig = getAgentConfig(chat.agent_name);
                  const AgentIcon = agentConfig.icon;
                  const totalMessages = (chat.user_message_count || 0) + (chat.assistant_message_count || 0);

                  return (
                    <button
                      key={chat.chat_id}
                      className={`${styles.chatCard} ${styles.chatCardEnded}`}
                      onClick={() => handleOpenChat(chat.chat_id)}
                    >
                      <div className={`${styles.cardAccent} ${agentConfig.accentClass}`} />

                      <div className={styles.cardBody}>
                        <div className={styles.cardTopRow}>
                          <div className={`${styles.agentTag} ${agentConfig.colorClass}`}>
                            <AgentIcon size={14} />
                            <span>{agentConfig.label}</span>
                          </div>
                          <span className={styles.timeAgo}>{getTimeAgo(chat.last_message_at || chat.created_at)}</span>
                        </div>

                        {chat.last_message_preview ? (
                          <p className={styles.messagePreview}>
                            {truncatePreview(chat.last_message_preview)}
                          </p>
                        ) : (
                          <p className={styles.messagePreviewEmpty}>
                            Conversation started
                          </p>
                        )}

                        <div className={styles.cardStatsRow}>
                          <div className={styles.messageCounts}>
                            {totalMessages > 0 && (
                              <span className={styles.totalMessages}>
                                {totalMessages} message{totalMessages !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div className={styles.viewArrow}>
                            <span>View</span>
                            <ChevronRight size={16} />
                          </div>
                        </div>

                        <div className={styles.cardDate}>
                          <span>{formatDate(chat.created_at, 'long')}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
};

export default Chats;